import { createContext, ReactNode, useContext, useState } from 'react';
import { ValidateableCvSections } from '../types/cvSections';

interface ValidationContextType {
  validationResults: Record<ValidateableCvSections, string | null>;
  isValidating: Record<ValidateableCvSections, boolean>;
  setValidationResult: (section: ValidateableCvSections, result: string | null) => void;
  setSectionValidating: (section: ValidateableCvSections, isValidating: boolean) => void;
}

const ValidationContext = createContext<ValidationContextType | undefined>(undefined);

export function ValidationProvider({ children }: { children: ReactNode }) {
  const [validationResults, setValidationResults] = useState<Record<ValidateableCvSections, string | null>>({
    [ValidateableCvSections.Summary]: null,
    [ValidateableCvSections.ProjectExperience]: null,
    [ValidateableCvSections.WorkExperience]: null,
    [ValidateableCvSections.Education]: null,
    [ValidateableCvSections.Certifications]: null,
    [ValidateableCvSections.Languages]: null,
    [ValidateableCvSections.Trainings]: null
  });

  const [isValidating, setIsValidating] = useState<Record<ValidateableCvSections, boolean>>({
    [ValidateableCvSections.Summary]: false,
    [ValidateableCvSections.ProjectExperience]: false,
    [ValidateableCvSections.WorkExperience]: false,
    [ValidateableCvSections.Education]: false,
    [ValidateableCvSections.Certifications]: false,
    [ValidateableCvSections.Languages]: false,
    [ValidateableCvSections.Trainings]: false
  });

  const setValidationResult = (section: ValidateableCvSections, result: string | null) => {
    setValidationResults(prev => ({
      ...prev,
      [section]: result
    }));
  };

  const setSectionValidating = (section: ValidateableCvSections, value: boolean) => {
    setIsValidating(prev => ({
      ...prev,
      [section]: value
    }));
  };

  return (
    <ValidationContext.Provider value={{
      validationResults,
      isValidating,
      setValidationResult,
      setSectionValidating
    }}>
      {children}
    </ValidationContext.Provider>
  );
}

export function useValidation() {
  const context = useContext(ValidationContext);
  if (context === undefined) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
} 
